/**
 * Validates if a string looks like a real human name.
 * Rejects random character sequences, repeated chars, and gibberish.
 */
export function isValidName(name: string): { valid: boolean; reason?: string } {
  const trimmed = name.trim();
  
  if (!trimmed || trimmed.length < 3) {
    return { valid: false, reason: "O nome deve ter pelo menos 3 caracteres" };
  }

  if (trimmed.length > 100) {
    return { valid: false, reason: "O nome é muito longo" };
  }

  // Must contain at least 2 words (first + last name)
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  if (words.length < 2) {
    return { valid: false, reason: "Informe nome e sobrenome" };
  }

  // Each word must have at least 2 characters
  if (words.some(w => w.length < 2)) {
    return { valid: false, reason: "Cada parte do nome deve ter pelo menos 2 caracteres" };
  }

  // Only letters, spaces, hyphens, apostrophes, and accented chars
  if (!/^[a-zA-ZÀ-ÿ\s'\-]+$/.test(trimmed)) {
    return { valid: false, reason: "O nome deve conter apenas letras" };
  }

  // Check for excessive repeated characters (e.g., "aaaa", "jjjj")
  if (/(.)\1{2,}/i.test(trimmed.replace(/\s/g, ''))) {
    return { valid: false, reason: "O nome contém caracteres repetidos em excesso" };
  }

  // Check for repeated 2-3 char patterns (e.g., "asdasd", "ababab", "xyzxyz")
  const noSpaces = trimmed.replace(/\s/g, '').toLowerCase();
  if (/(.{2,3})\1{1,}/i.test(noSpaces)) {
    return { valid: false, reason: "O nome não parece ser válido" };
  }

  // Check consonant clusters (gibberish detection) - more than 4 consonants in a row
  if (/[bcdfghjklmnpqrstvwxyz]{5,}/i.test(noSpaces)) {
    return { valid: false, reason: "O nome não parece ser válido" };
  }

  // Check for keyboard patterns (qwer, asdf, zxcv, etc.)
  const keyboardPatterns = ['qwer', 'asdf', 'zxcv', 'qwert', 'asdfg', 'zxcvb', 'tyui', 'ghjk', 'bnm'];
  if (keyboardPatterns.some(p => noSpaces.includes(p))) {
    return { valid: false, reason: "O nome não parece ser válido" };
  }

  // Check vowel ratio - real names have vowels
  const vowels = (noSpaces.match(/[aeiouáàâãéèêíïóôõúü]/gi) || []).length;
  const ratio = vowels / noSpaces.length;
  if (ratio < 0.15) {
    return { valid: false, reason: "O nome não parece ser válido" };
  }

  // Check each word individually for gibberish patterns
  for (const word of words) {
    const w = word.toLowerCase();
    // Repeated 2-char pattern in a single word
    if (/(.{2})\1{1,}/i.test(w)) {
      return { valid: false, reason: "O nome não parece ser válido" };
    }
    // Too many unique consonants in sequence without common patterns
    if (w.length >= 5) {
      const wordVowels = (w.match(/[aeiouáàâãéèêíïóôõúü]/gi) || []).length;
      const wordRatio = wordVowels / w.length;
      if (wordRatio < 0.2) {
        return { valid: false, reason: "O nome não parece ser válido" };
      }
    }
  }

  return { valid: true };
}

/**
 * Validates if a string looks like a real restaurant/business name.
 * More permissive than human names (allows numbers, &, etc.)
 */
export function isValidRestaurantName(name: string): { valid: boolean; reason?: string } {
  const trimmed = name.trim();

  if (!trimmed || trimmed.length < 3) {
    return { valid: false, reason: "O nome deve ter pelo menos 3 caracteres" };
  }

  if (trimmed.length > 100) {
    return { valid: false, reason: "O nome é muito longo" };
  }

  // Allow letters, numbers, spaces, common special chars
  if (!/^[a-zA-ZÀ-ÿ0-9\s&'.\-#!@]+$/.test(trimmed)) {
    return { valid: false, reason: "O nome contém caracteres inválidos" };
  }

  // Must have at least some letters
  const letters = (trimmed.match(/[a-zA-ZÀ-ÿ]/g) || []).length;
  if (letters < 3) {
    return { valid: false, reason: "O nome deve conter pelo menos 3 letras" };
  }

  // Check for excessive repeated characters
  if (/(.)\1{3,}/i.test(trimmed.replace(/\s/g, ''))) {
    return { valid: false, reason: "O nome contém caracteres repetidos em excesso" };
  }

  // Check consonant clusters (gibberish detection)
  const noSpaces = trimmed.replace(/[\s0-9&'.#!@\-]/g, '').toLowerCase();
  if (noSpaces.length > 4 && /[bcdfghjklmnpqrstvwxyz]{5,}/i.test(noSpaces)) {
    return { valid: false, reason: "O nome do restaurante não parece ser válido" };
  }

  // Check vowel ratio for gibberish
  if (noSpaces.length > 4) {
    const vowels = (noSpaces.match(/[aeiouáàâãéèêíïóôõúü]/gi) || []).length;
    const ratio = vowels / noSpaces.length;
    if (ratio < 0.12) {
      return { valid: false, reason: "O nome do restaurante não parece ser válido" };
    }
  }

  return { valid: true };
}

/**
 * Validates Brazilian phone number format.
 * Accepts: (XX) XXXXX-XXXX or (XX) XXXX-XXXX or just digits
 */
export function isValidBrazilianPhone(phone: string): { valid: boolean; reason?: string } {
  const trimmed = phone.trim();
  if (!trimmed) {
    return { valid: false, reason: "Informe o número de telefone" };
  }

  // Extract only digits
  const digits = trimmed.replace(/\D/g, '');

  // Brazilian phone: 10 or 11 digits (with DDD)
  if (digits.length < 10 || digits.length > 11) {
    return { valid: false, reason: "O telefone deve ter 10 ou 11 dígitos (com DDD)" };
  }

  // DDD must be valid (11-99)
  const ddd = parseInt(digits.substring(0, 2));
  if (ddd < 11 || ddd > 99) {
    return { valid: false, reason: "DDD inválido" };
  }

  // For 11 digits, must start with 9 after DDD (mobile)
  if (digits.length === 11 && digits[2] !== '9') {
    return { valid: false, reason: "Celular deve começar com 9 após o DDD" };
  }

  // Reject all same digits (e.g., 11111111111)
  if (/^(\d)\1+$/.test(digits)) {
    return { valid: false, reason: "Número de telefone inválido" };
  }

  // Reject sequential digits
  if (/^(0123456789|1234567890|9876543210)/.test(digits.substring(2))) {
    return { valid: false, reason: "Número de telefone inválido" };
  }

  return { valid: true };
}

/**
 * Validates email with stricter rules than just format.
 * Checks for disposable domains, gibberish local parts, etc.
 */
export function isValidEmail(email: string): { valid: boolean; reason?: string } {
  const trimmed = email.trim().toLowerCase();

  if (!trimmed) {
    return { valid: false, reason: "Informe o e-mail" };
  }

  // Basic format check
  const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, reason: "Formato de e-mail inválido" };
  }

  const [localPart, domain] = trimmed.split('@');

  // Local part must have at least 3 chars
  if (localPart.length < 3) {
    return { valid: false, reason: "E-mail muito curto" };
  }

  // Check for gibberish in local part
  const cleanLocal = localPart.replace(/[0-9._+\-]/g, '');
  if (cleanLocal.length > 4) {
    // Vowel ratio check
    const vowels = (cleanLocal.match(/[aeiou]/gi) || []).length;
    const ratio = vowels / cleanLocal.length;
    if (ratio < 0.15) {
      return { valid: false, reason: "O e-mail não parece ser válido" };
    }
    // Repeated patterns (asdasd, ababab)
    if (/(.{2,3})\1{1,}/i.test(cleanLocal)) {
      return { valid: false, reason: "O e-mail não parece ser válido" };
    }
    // Keyboard patterns
    const kbPatterns = ['qwer', 'asdf', 'zxcv', 'qwert', 'asdfg', 'zxcvb'];
    if (kbPatterns.some(p => cleanLocal.toLowerCase().includes(p))) {
      return { valid: false, reason: "O e-mail não parece ser válido" };
    }
  }

  // Check domain for gibberish
  const domainName = domain.split('.')[0];
  if (domainName.length > 3) {
    if (/(.{2,3})\1{1,}/i.test(domainName)) {
      return { valid: false, reason: "O domínio do e-mail não parece ser válido" };
    }
    const domVowels = (domainName.match(/[aeiou]/gi) || []).length;
    if (domVowels / domainName.length < 0.15) {
      return { valid: false, reason: "O domínio do e-mail não parece ser válido" };
    }
  }

  // Block common disposable/temporary email domains
  const disposableDomains = [
    'tempmail.com', 'throwaway.email', 'guerrillamail.com', 'mailinator.com',
    'yopmail.com', 'tempail.com', 'fakeinbox.com', 'sharklasers.com',
    'guerrillamailblock.com', 'grr.la', 'dispostable.com', 'trashmail.com',
    'maildrop.cc', 'temp-mail.org', '10minutemail.com', 'minutemail.com',
  ];

  if (disposableDomains.includes(domain)) {
    return { valid: false, reason: "Não aceitamos e-mails temporários" };
  }

  return { valid: true };
}
